import { metaData } from 'config/config';
import { formatISO } from 'date-fns';
import wrappers from 'google-protobuf/google/protobuf/wrappers_pb';

import {
  GetBreadCrumbsRequest,
  GetInvoiceSettingsRequest,
  GetRecipientsRequest,
  GetTenantByPortfolioIDsRequest,
  GetTenantHeaderRequest,
  GetTenantsInvoicesRequest,
  InvoiceSettings,
  SaveInvoiceSettingsRequest,
  SyncRecipientsRequest,
  GetTenantInvoicesRequest,
  InvoiceAction,
  ExecuteInvoiceActionRequest,
  DownloadInvoiceRequest
} from '../../api/aggregation_grpc_web_pb';
import {
  CreateTenantRequest,
  DeleteTenantRequest,
  GetTenantRequest,
  GetTenantsRequest,
  GetTotalRentByAssetAndYearRequest,
  UpdateTenantRequest
} from '../../api/tenant_grpc_web_pb';
import { capitalize } from '../../helpers/utils';
import { AggregationService } from '../services/aggregation';
import { ContractClauseService, TenantService } from '../services/tenant';

import * as actionTypes from './actionTypes';

const getTenantsStart = () => ({
  type: actionTypes.GET_TENANTS_START
});

const getTenantsSuccess = (tenants, bundleId) => ({
  type: actionTypes.GET_TENANTS_SUCCESS,
  tenants,
  bundleId
});

const getTenantsFail = (error) => ({
  type: actionTypes.GET_TENANTS_FAIL,
  error
});

const getInvoicesStart = () => ({
  type: actionTypes.GET_INVOICES_START
});

const getInvoicesSuccess = (invoices) => ({
  type: actionTypes.GET_INVOICES_SUCCESS,
  data: invoices
});

const getInvoicesFail = (error) => ({
  type: actionTypes.GET_INVOICES_FAIL,
  error
});

const getInvoiceSettingsSuccess = (data) => ({
  type: actionTypes.GET_INVOICE_SETTINGS_SUCCESS,
  data
});

const getInvoiceSettingsFail = (error) => ({
  type: actionTypes.GET_INVOICE_SETTINGS_FAIL,
  error
});

const getInvoiceRecipientsSuccess = (data) => ({
  type: actionTypes.GET_INVOICE_RECIPIENTS_SUCCESS,
  data
});

const getInvoiceRecipientsFail = (error) => ({
  type: actionTypes.GET_INVOICE_RECIPIENTS_FAIL,
  error
});

const getTenantInvoicesSuccess = (data) => ({
  type: actionTypes.GET_TENANT_INVOICE_SUCCESS,
  data
});

const getTenantInvoicesFail = (error) => ({
  type: actionTypes.GET_TENANT_INVOICE_FAIL,
  error
});

export const updateTenant = (form) => async () => {
  const request = new UpdateTenantRequest();
  request.setId(form.id);
  request.setTenantcontractid(form.tenantcontractid);

  const companyname = new wrappers.StringValue();
  companyname.setValue(form.companyname);
  request.setCompanyname(companyname);

  const tradingname = new wrappers.StringValue();
  tradingname.setValue(form.tradingname);
  request.setTradingname(tradingname);

  const industrytype = new wrappers.StringValue();
  industrytype.setValue(form.industry);
  request.setIndustry(industrytype);

  const contractenddate = new wrappers.StringValue();
  const endDate = formatISO(new Date(form.contractenddate), { representation: 'date' });
  contractenddate.setValue(endDate);
  request.setContractenddate(contractenddate);

  const contractstartdate = new wrappers.StringValue();
  const startDate = formatISO(new Date(form.contractstartdate), { representation: 'date' });
  contractstartdate.setValue(startDate);
  request.setContractstartdate(contractstartdate);
  form.unitidsList.forEach((id) => {
    request.addUnitids(id);
  });

  const restore = new wrappers.BoolValue();
  restore.setValue(true);
  request.setRestore(restore);

  const headOfficeAddress = new wrappers.StringValue();
  headOfficeAddress.setValue(form.headOfficeAddress);
  request.setHeadOfficeAddress(headOfficeAddress);

  const notes = new wrappers.StringValue();
  notes.setValue(form.notes);
  request.setNotes(notes);

  const mainContact = new wrappers.StringValue();
  mainContact.setValue(form.mainContact);
  request.setMainContact(mainContact);

  const position = new wrappers.StringValue();
  position.setValue(form.position);
  request.setPosition(position);

  const email = new wrappers.StringValue();
  email.setValue(form.email);
  request.setEmail(email);

  const phone = new wrappers.StringValue();
  phone.setValue(form.phone);
  request.setPhone(phone);

  const comment = new wrappers.StringValue();
  comment.setValue(form.comment);
  request.setComment(comment);

  try {
    const result = await TenantService.updateTenant(request, metaData());
    const response = result.toObject();

    return response;
  } catch (error) {
    throw error.message;
  }
};

export const createTenant = (form, assetId) => async () => {
  const request = new CreateTenantRequest();
  request.setCompanyname(form.companyname);
  request.setTradingname(form.tradingname);
  request.setIndustry(capitalize(form.industry));
  request.setAssetid(assetId);

  const headOfficeAddress = new wrappers.StringValue();
  headOfficeAddress.setValue(form.headOfficeAddress);
  request.setHeadOfficeAddress(headOfficeAddress);

  const notes = new wrappers.StringValue();
  notes.setValue(form.notes);
  request.setNotes(notes);

  const mainContact = new wrappers.StringValue();
  mainContact.setValue(form.mainContact);
  request.setMainContact(mainContact);

  const position = new wrappers.StringValue();
  position.setValue(form.position);
  request.setPosition(position);

  const email = new wrappers.StringValue();
  email.setValue(form.email);
  request.setEmail(email);

  const phone = new wrappers.StringValue();
  phone.setValue(form.phone);
  request.setPhone(phone);

  const comment = new wrappers.StringValue();
  comment.setValue(form.comment);
  request.setComment(comment);

  const enddate = new wrappers.StringValue();
  const endDate = formatISO(new Date(form.contractenddate), { representation: 'date' });
  enddate.setValue(endDate);
  request.setEnddate(enddate);

  const startdate = new wrappers.StringValue();
  const startDate = formatISO(new Date(form.contractstartdate), { representation: 'date' });
  startdate.setValue(startDate);
  request.setStartdate(startdate);
  form.unitidsList.forEach((id) => {
    request.addUnitids(id);
  });

  try {
    const result = await TenantService.createTenant(request, metaData());
    const response = result.toObject();

    return response;
  } catch (error) {
    throw error.message;
  }
};

export const setTenantsBundle = (bundleId) => ({ type: actionTypes.SET_TENANTS_BUNDLE, bundleId });

export const getTenants =
  (portfolioIds, bundleId, assetIds, portfolioFilters) => async (dispatch) => {
    dispatch(getTenantsStart());

    if (portfolioFilters) {
      const request = new GetTenantByPortfolioIDsRequest();
      request.setPortfoliosList(portfolioIds || []);

      try {
        const result = await AggregationService.getTenantByPortfolioIDs(request, metaData());
        const response = result.toObject().tenantsList;
        dispatch(getTenantsSuccess(response, bundleId));

        return response;
      } catch (error) {
        dispatch(getTenantsFail(error.message));

        throw error.message;
      }
    }

    const request = new GetTenantsRequest();
    request.setAssetidsList(assetIds || []);

    try {
      const result = await TenantService.getTenants(request, metaData());
      const response = result.toObject().tenantsList;
      dispatch(getTenantsSuccess(response, bundleId));
      return response;
    } catch (error) {
      dispatch(getTenantsFail(error.message));

      throw error.message;
    }
  };

// Get Tenants Details
const getTenantDetailsStart = () => ({
  type: actionTypes.GET_TENANTS_DETAILS_START
});

const getTenantDetailsSuccess = (tenantDetails) => ({
  type: actionTypes.GET_TENANTS_DETAILS_SUCCESS,
  tenantDetails
});

const getTenantDetailsFail = (error) => ({
  type: actionTypes.GET_TENANTS_DETAILS_FAIL,
  error
});

export const getTenantDetails = (id) => async (dispatch) => {
  dispatch(getTenantDetailsStart());
  const request = new GetTenantRequest();
  request.setId(id);

  try {
    const result = await TenantService.getTenant(request, metaData());
    const response = result.toObject().tenant;
    dispatch(getTenantDetailsSuccess(response));

    return response;
  } catch (error) {
    dispatch(getTenantDetailsFail(error.message));

    throw error.message;
  }
};

export const deleteTenant = (id) => async () => {
  const request = new DeleteTenantRequest();
  request.setId(id);

  try {
    const result = await TenantService.deleteCTenant(request, metaData());
    const response = result.toObject();
    return response;
  } catch (error) {
    throw error.message;
  }
};

// Get Tenants Header
const getTenantHeaderStart = () => ({
  type: actionTypes.GET_TENANT_HEADER_START
});

const getTenantHeaderSuccess = (header) => ({
  type: actionTypes.GET_TENANT_HEADER_SUCCESS,
  header
});

const getTenantHeaderFail = (error) => ({
  type: actionTypes.GET_TENANT_HEADER_FAIL,
  error
});

export const getTenantHeader = (id) => async (dispatch) => {
  dispatch(getTenantHeaderStart());
  const request = new GetTenantHeaderRequest();
  request.setTenantid(id);

  try {
    const result = await AggregationService.getTenantHeader(request, metaData());
    const response = result.toObject();

    dispatch(getTenantHeaderSuccess(response));

    return response;
  } catch (error) {
    dispatch(getTenantHeaderFail(error.message));
    return error.message;
  }
};

// Get Tenants Breadcrumps
const getTenantCrumpsStart = () => ({
  type: actionTypes.GET_BREADCRUMPS_START
});

const getTenantCrumpsSuccess = (crumps) => ({
  type: actionTypes.GET_BREADCRUMPS_SUCCESS,
  crumps
});

const getTenantCrumpsFail = (error) => ({
  type: actionTypes.GET_BREADCRUMPS_FAIL,
  error
});

export const getTenantCrumps = (id) => async (dispatch) => {
  dispatch(getTenantCrumpsStart());
  const request = new GetBreadCrumbsRequest();
  request.setTenantid(id);

  try {
    const result = await AggregationService.getBreadCrumbs(request, metaData());
    const response = result.toObject();

    dispatch(getTenantCrumpsSuccess(response));

    return response;
  } catch (error) {
    dispatch(getTenantCrumpsFail(error.message));
    throw error.message;
  }
};

const getTotalRentByAssetAndYearStart = () => ({
  type: actionTypes.GET_TOTAL_RENT_BY_ASSET_AND_YEAR_START
});

const getTotalRentByAssetAndYearSuccess = (totalRentByAsset) => ({
  type: actionTypes.GET_TOTAL_RENT_BY_ASSET_AND_YEAR_SUCCESS,
  totalRentByAsset
});

const getTotalRentByAssetAndYearFail = (error) => ({
  type: actionTypes.GET_TOTAL_RENT_BY_ASSET_AND_YEAR_FAIL,
  error
});

export const getTotalRentByAssetAndYear = (assetIDs, year) => async (dispatch) => {
  dispatch(getTotalRentByAssetAndYearStart());

  const request = new GetTotalRentByAssetAndYearRequest();
  request.setAssetidList(assetIDs);
  request.setYear(year);
  await ContractClauseService.getTotalRentByAssetAndYear(request, metaData(), (err, response) => {
    if (err) {
      dispatch(getTotalRentByAssetAndYearFail(err));
    } else {
      dispatch(getTotalRentByAssetAndYearSuccess(response.toObject().calculationList));
    }
  });
};

export const getInvoices = (portfolioIds, assetIds) => async (dispatch) => {
    dispatch(getTenantsStart());

    const request = new GetTenantsInvoicesRequest();
    request.setPortfoliosList(portfolioIds || []);
    request.setAssetsList(assetIds || []);

    try {
      const result = await AggregationService.getTenantsInvoices(request, metaData());
      const response = result.toObject().tenantsList;
      dispatch(getInvoicesSuccess(response));
      return response;
    } catch (error) {
      dispatch(getInvoicesFail(error.message));

      throw error.message;
    }
  };

export const getInvoiceSettings = () => async (dispatch) => {
  const request = new GetInvoiceSettingsRequest();

  try {
    const result = await AggregationService.getInvoiceSettings(request, metaData());
    const response = result.toObject().settings;
    dispatch(getInvoiceSettingsSuccess(response));
    return response;
  } catch (error) {
    dispatch(getInvoiceSettingsFail(error.message));
    throw error.message;
  }
};

export const saveInvoiceSettings = (form) => async () => {
  const request = new SaveInvoiceSettingsRequest();
  const settings = new InvoiceSettings();
  settings.setCompanyName(form.company_name);
  settings.setCompanyAddress(form.company_address);
  settings.setCompanyEmail(form.company_email);
  settings.setCompanyPhone(form.company_phone);
  settings.setInvoiceNumberPrefix(form.invoice_number_prefix);
  settings.setPaymentTerms(form.payment_terms);
  settings.setPaymentInstructions(form.payment_instructions);
  settings.setFooterNotes(form.footer_notes);
  const days = parseInt(form.invoice_date_configuration, 10);
  settings.setInvoiceDateConfiguration(isNaN(days) ? 0 : days);
  request.setSettings(settings);

  try {
    const result = await AggregationService.saveInvoiceSettings(request, metaData());
    const response = result.toObject();
    return response;
  } catch (error) {
    throw error.message;
  }
}

export const getInvoiceRecipients = (tenantId) => async (dispatch) => {
  const request = new GetRecipientsRequest();
  request.setTenantid(tenantId);

  try {
    const result = await AggregationService.getRecipients(request, metaData());
    const response = result.toObject();
    const emails = response.recipientsList;
    const primary = response.primaryrecipient;
    dispatch(getInvoiceRecipientsSuccess({ emails, primary }));
    return response;
  } catch (error) {
    dispatch(getInvoiceRecipientsFail(error.message));
    throw error.message;
  }
};

export const syncInvoiceRecipients = (tenantId, newRecipientsList, primaryRecipient) => async (dispatch) => {
  const request = new SyncRecipientsRequest();
  request.setTenantid(tenantId);
  request.setRecipientsList(newRecipientsList);
  request.setPrimaryrecipient(primaryRecipient);

  try {
    const result = await AggregationService.syncRecipients(request, metaData());
    return result.toObject();
  } catch (error) {
    throw error.message;
  }
};

export const getTenantInvoices = (tenantId) => async (dispatch) => {
  const request = new GetTenantInvoicesRequest();
  request.setTenantid(tenantId);

  try {
    const result = await AggregationService.getTenantInvoices(request, metaData());
    const response = result.toObject().tenantsList;
    dispatch(getTenantInvoicesSuccess(response));
    return response;
  } catch (error) {
    dispatch(getTenantInvoicesFail(error.message));
    throw error.message;
  }
};

export const executeInvoiceAction = (invoiceId, action) => async (dispatch) => {
  const request = new ExecuteInvoiceActionRequest();
  request.setId(invoiceId);
  request.setAction(getActionValue(action));

  try {
    const result = await AggregationService.executeInvoiceAction(request, metaData());
    return result.toObject();
  } catch (error) {
    throw error.message;
  }
};

const getActionValue = (action) => {
  switch (action) {
    case 'APPROVE':
      return InvoiceAction.APPROVE;
    case 'SEND':
      return InvoiceAction.SEND;
    case 'PAID':
      return InvoiceAction.PAID;
  }
}

export const viewInvoicePdf = (invoiceId) => async (dispatch) => {
  const request = new DownloadInvoiceRequest();
  request.setInvoiceId(invoiceId);

  try {
    const response = await AggregationService.downloadInvoice(request, metaData());

    const fileBytes = response.getFileContent();
    const contentType = response.getContentType() || 'application/pdf';

    const blob = new Blob([fileBytes], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    window.open(url, '_blank');

    return { success: true };
  } catch (error) {
    console.error("Failed to view invoice:", error);
    throw error.message;
  }
};

export const downloadInvoicePdf = (invoiceId) => async (dispatch) => {
  const request = new DownloadInvoiceRequest();
  request.setInvoiceId(invoiceId);

  try {
    const response = await AggregationService.downloadInvoice(request, metaData());

    const fileBytes = response.getFileContent();
    const fileName = response.getFileName();
    const contentType = response.getContentType() || 'application/pdf';

    const blob = new Blob([fileBytes], { type: contentType });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, fileName };
  } catch (error) {
    console.error("Failed to download invoice:", error);
    throw error.message;
  }
};